import React from 'react';
import type { FontSize } from './Text';

const getFontSize = (size: FontSize): string => {
    switch (size) {
        case 'xsmall':
            return '12px';
        case 'small':
            return '14px';
        case 'medium':
            return '16px';
        case 'large':
            return '18px';
        case 'xlarge':
            return '20px';
        case 'xxlarge':
            return '24px';
        default:
            return '14px';
    }
};

export type CodeTextVariant = 'block' | 'inline';

export interface CodeTextProps {
    bold?: boolean;
    size?: FontSize;
    hasBorder?: boolean;
    variant?: CodeTextVariant;
    color?: string;
    backgroundColor?: string;
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}

const CodeText: React.FC<CodeTextProps> = ({
    bold = false,
    children,
    size = 'small',
    hasBorder = false,
    variant = 'block',
    // Deliberately NOT `inherit`: secondary prose would drop the chip to 3.65:1 in dark mode,
    // under AA's 4.5:1. Pinning primary measures 18.5:1 light / 9.4:1 dark.
    color = 'var(--malachite-text-primary)',
    backgroundColor,
    className = '',
    style = {},
}) => {
    const isInline = variant === 'inline';
    const codeStyles: React.CSSProperties = {
        fontFamily: 'var(--malachite-font-mono, monospace)',
        fontSize: isInline ? '0.9em' : getFontSize(size),
        fontWeight: bold ? 600 : 400,
        color,
        backgroundColor: hasBorder
            ? 'transparent'
            : backgroundColor || 'var(--malachite-card-dimmed-background, #f5f5f5)',
        borderRadius: isInline ? '4px' : '8px',
        border: hasBorder ? 'none' : '1px solid var(--malachite-card-border, #e8e8e8)',
        padding: hasBorder ? '0px' : isInline ? '0 4px' : '4px 8px',
        display: 'inline',
        ...style,
    };

    return (
        <code className={className} style={codeStyles}>
            {children}
        </code>
    );
};

export default CodeText;
